import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import {
  SidebarContainer,
  LogoWrapper,
  SearchInput,
  MenuItem,
  MenuLink,
  LeftBar,
  RightBar,
  IconButton,
  ModuloLabel,
  ScrollContainer,
  InputArea,
  SidebarSectionLabelWithSeparator,
  VerticalSeparator,
} from './styles';

import Logo from '../../assets/img/LOGO_KONVIX_H.png';
import LogoCollapsed from '../../assets/img/logo_white.png';
import { NavigationContext } from '~/context/navigation';
import Spinner from '../Spinner';
import { useLayout } from '../Layout/LayoutContext';
import {
  FaSearch,
  FaTimes,
  FaEllipsisV,
  FaStar,
  FaRegStar,
} from 'react-icons/fa';
import { InformacaoTelasAcessadasContext } from '~/context/InformacaoTelasAcessadas';
import { MenusProvider, MenuButtons, MenuContent, useMenus } from './Menus';
import { StorageService } from '~/utils/classes/StorageService';
import { useOpenInNewTab } from '~/hooks/useOpenInNewTab';

const SidebarContent: React.FC = () => {
  const {
    telas,
    selectedModulo,
    setSelectedModulo,
    moduloSelecionado,
    collapsed,
    setCollapsed,
  } = useLayout();
  const { addScreen } = useContext(NavigationContext);
  const { addScreenInfo } = useContext(InformacaoTelasAcessadasContext);
  const {
    viewMode,
    setViewMode,
    handleFavorite,
    handleAddAccessed,
    searchTerm,
    setSearchTerm,
  } = useMenus();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const openInNewTab = useOpenInNewTab();

  const store = new StorageService();

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Ref para controlar o scroll da RightBar
  const rightBarRef = useRef<HTMLDivElement>(null);

  // UseEffect para scroll ao topo quando mudar de módulo
  useEffect(() => {
    if (rightBarRef.current && selectedModulo) {
      rightBarRef.current.scrollTop = 0;
    }
  }, [selectedModulo]);

  useEffect(() => {
    if (!collapsed) {
      const raf = requestAnimationFrame(() => {
        const el = searchInputRef.current;
        if (el) {
          el.focus({ preventScroll: true });
          if (el.value) el.select();
        }
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [collapsed]);

  const handleClickToggle = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    store.save('session', 'collapsed', newCollapsed);

    // Se está colapsando (newCollapsed = true), voltar para o módulo base
    if (newCollapsed) {
      setViewMode('modules');
      setSelectedModulo(2);
    }
  };

  const filteredModulos = useMemo(() => {
    if (!searchTerm) return [];
    const removeCharacteres = (text: string) =>
      text
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    return telas
      .map((modulo) => ({
        ...modulo,
        telas: modulo.telas.filter((tela: any) =>
          removeCharacteres(tela.des_tela).includes(
            removeCharacteres(searchTerm),
          ),
        ),
      }))
      .filter((modulo) => modulo.telas.length > 0);
  }, [telas, searchTerm]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  const toggleDropdown = (e: React.MouseEvent, telaId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(openDropdown === telaId ? null : telaId);
  };

  const handleFavoriteClick = async (e: React.MouseEvent, tela: any) => {
    e.preventDefault();
    e.stopPropagation();

    const favoriteStatus = tela.flg_favorita;
    await handleFavorite(tela.cod_tela, favoriteStatus);
    setOpenDropdown(null);
  };

  const handleTelaNewTabClick = (tela: any) => {
    if (tela.content) {
      handleAddAccessed(tela.cod_tela);

      setCollapsed(true);
      store.save('session', 'collapsed', true);

      // Sempre resetar para o estado padrão (modules) com módulo base (2) ao colapsar
      setViewMode('modules');
      setSelectedModulo(2);
    }
  };

  const handleTelaClick = (tela: any) => {
    if (tela.content) {
      addScreen(tela);
      addScreenInfo(tela);
      handleTelaNewTabClick(tela);
    }
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Resetar para o módulo base quando o menu for colapsado externamente (navbar)
  useEffect(() => {
    if (collapsed) {
      setSearchTerm('');
      setViewMode('modules');
      setSelectedModulo(2);
    }
  }, [collapsed, setViewMode, setSelectedModulo, setSearchTerm]);

  return (
    <SidebarContainer collapsed={collapsed}>
      {/* Logo */}
      <LogoWrapper onClick={handleClickToggle}>
        <img
          src={collapsed ? LogoCollapsed : Logo}
          alt="Logo"
          height={collapsed ? 30 : 40}
        />
      </LogoWrapper>

      {/* Busca */}
      <InputArea collapsed={collapsed}>
        <FaSearch />
        <SearchInput
          ref={searchInputRef}
          placeholder={collapsed ? '' : 'Buscar...'}
          value={searchTerm.toUpperCase()}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={() => {
            if (collapsed) {
              setCollapsed(false);
              store.save('session', 'collapsed', false);
            }
          }}
          collapsed={collapsed}
        />
        {searchTerm && <FaTimes onClick={clearSearch} />}
      </InputArea>

      {telas.length <= 0 && <Spinner />}

      {/* Exibição dos Menus */}
      {telas.length > 0 && (
        <ScrollContainer>
          {/* Exibição de Menu Filtrado */}
          {searchTerm ? (
            <div
              style={{
                display: 'flex',
                flex: '1',
                height: '100%',
              }}
            >
              {/* Durante a busca, só mostra a RightBar com os resultados */}
              <RightBar style={{ width: '100%' }}>
                {filteredModulos.length > 0 &&
                  filteredModulos.map((modulo) => (
                    <React.Fragment key={modulo?.cod_modulo}>
                      <SidebarSectionLabelWithSeparator>
                        <span style={{ fontWeight: 500, color: '#c2a0f8' }}>
                          {modulo?.des_modulo}
                        </span>
                        <div className="hr-separator" />
                      </SidebarSectionLabelWithSeparator>
                      {modulo?.telas.map((tela: any) => (
                        <MenuItem key={tela.cod_tela}>
                          <MenuLink
                            to={tela.url || '/app'}
                            onClick={(e) => {
                              // Se clicou no botão de ações, não abrir a tela
                              if (
                                (e.target as HTMLElement).closest(
                                  '.actions-button',
                                )
                              ) {
                                e.preventDefault();
                                return;
                              }

                              if (Number(tela.cod_tela) === 283) {
                                e.preventDefault();
                                e.stopPropagation();
                                openInNewTab(tela.url);
                                handleTelaNewTabClick(tela);
                                return;
                              }

                              if (tela.content) {
                                handleTelaClick(tela);
                                setSearchTerm('');
                              }
                            }}
                            fontSize="16px"
                          >
                            {tela.Icon && <tela.Icon />}
                            {tela.des_tela}

                            {tela.flg_favorita && (
                              <span
                                className="favorite-indicator"
                                title="Tela favoritada"
                              />
                            )}

                            <button
                              type="button"
                              className="actions-button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleDropdown(e, tela.cod_tela.toString());
                              }}
                              title="Ações"
                            >
                              <FaEllipsisV size={12} />
                            </button>

                            {openDropdown === tela.cod_tela.toString() && (
                              <div className="dropdown">
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={(e) => handleFavoriteClick(e, tela)}
                                >
                                  {tela.flg_favorita ? (
                                    <>
                                      <FaStar size={14} />
                                      Remover dos favoritos
                                    </>
                                  ) : (
                                    <>
                                      <FaRegStar size={14} />
                                      Adicionar aos favoritos
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </MenuLink>
                        </MenuItem>
                      ))}
                    </React.Fragment>
                  ))}
                {filteredModulos.length === 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      color: '#fff',
                    }}
                  >
                    Nenhuma tela encontrada para &quot;{searchTerm}&quot;
                  </div>
                )}
              </RightBar>
            </div>
          ) : (
            /* Exibição dos Módulos ou Menus Especiais */
            <div
              style={{
                display: 'flex',
                flex: '1',
                height: '100%',
              }}
            >
              {/* Módulos */}
              <LeftBar collapsed={collapsed}>
                {/* Lista de Módulos */}
                {telas.map((modulo) => (
                  <IconButton
                    key={modulo.cod_modulo}
                    onClick={() => {
                      // Se a sidebar estiver recolhida, expande
                      if (collapsed) {
                        setCollapsed(false);
                        store.save('session', 'collapsed', false);
                        setSelectedModulo(modulo.cod_modulo);
                        setViewMode('modules');
                      }
                      // Se clicar no módulo já selecionado com sidebar aberta, fecha
                      else if (
                        selectedModulo === modulo.cod_modulo &&
                        viewMode === 'modules'
                      ) {
                        setSelectedModulo(2);
                        setCollapsed(true);
                        store.save('session', 'collapsed', true);
                      }
                      // Caso contrário, seleciona o módulo
                      else {
                        setSelectedModulo(modulo.cod_modulo);
                        setViewMode('modules');
                      }
                    }}
                    selected={
                      selectedModulo === modulo.cod_modulo &&
                      viewMode === 'modules'
                    }
                    title={modulo.des_modulo}
                  >
                    {modulo.Icon && <modulo.Icon size={20} />}
                    {!collapsed && (
                      <ModuloLabel collapsed={collapsed}>
                        {modulo.des_modulo}
                      </ModuloLabel>
                    )}
                  </IconButton>
                ))}

                {/* Divisor */}
                {!collapsed && (
                  <SidebarSectionLabelWithSeparator>
                    <div className="hr-separator" />
                  </SidebarSectionLabelWithSeparator>
                )}

                {/* Novos botões de funcionalidades */}
                <MenuButtons collapsed={collapsed} />
              </LeftBar>

              {/* Separador Vertical */}
              {!collapsed && (selectedModulo || viewMode !== 'modules') && (
                <VerticalSeparator />
              )}

              {/* Telas por Módulos */}
              {!collapsed &&
                selectedModulo &&
                viewMode === 'modules' &&
                moduloSelecionado && (
                  <RightBar ref={rightBarRef}>
                    {moduloSelecionado?.modulosSecao?.map((secao: any) => (
                      <React.Fragment key={secao.cod_seq_modulo_secao}>
                        <SidebarSectionLabelWithSeparator>
                          <span>{secao.des_modulo_secao}</span>
                          <div className="hr-separator" />
                        </SidebarSectionLabelWithSeparator>
                        {moduloSelecionado.telas
                          ?.filter(
                            (t: any) =>
                              t.cod_modulo_secao === secao.cod_seq_modulo_secao,
                          )
                          .map((tela: any) => {
                            const { Icon } = tela;

                            return (
                              <MenuItem key={tela.cod_tela}>
                                <MenuLink
                                  to={tela.url || '/app'}
                                  onClick={(e) => {
                                    // Se clicou no botão de ações, não abrir a tela
                                    if (
                                      (e.target as HTMLElement).closest(
                                        '.actions-button',
                                      )
                                    ) {
                                      e.preventDefault();
                                      return;
                                    }

                                    if (Number(tela.cod_tela) === 283) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openInNewTab(tela.url);
                                      handleTelaNewTabClick(tela);
                                      return;
                                    }

                                    handleTelaClick(tela);
                                  }}
                                >
                                  {Icon && <Icon size={18} />}
                                  {tela.des_tela}

                                  {tela.flg_favorita && (
                                    <span
                                      className="favorite-indicator"
                                      title="Tela favoritada"
                                    />
                                  )}

                                  <button
                                    type="button"
                                    className="actions-button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleDropdown(
                                        e,
                                        tela.cod_tela.toString(),
                                      );
                                    }}
                                    title="Ações"
                                  >
                                    <FaEllipsisV size={12} />
                                  </button>

                                  {openDropdown ===
                                    tela.cod_tela.toString() && (
                                    <div className="dropdown">
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={(e) =>
                                          handleFavoriteClick(e, tela)
                                        }
                                      >
                                        {tela.flg_favorita ? (
                                          <>
                                            <FaStar size={14} />
                                            Remover dos favoritos
                                          </>
                                        ) : (
                                          <>
                                            <FaRegStar size={14} />
                                            Adicionar aos favoritos
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </MenuLink>
                              </MenuItem>
                            );
                          })}
                      </React.Fragment>
                    ))}
                  </RightBar>
                )}

              {/* Conteúdo dos Novos Menus */}
              <MenuContent />
            </div>
          )}
        </ScrollContainer>
      )}
    </SidebarContainer>
  );
};

const Sidebar: React.FC = () => {
  return (
    <MenusProvider>
      <SidebarContent />
    </MenusProvider>
  );
};

export default Sidebar;
